import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Box, Card, ElementContainer, MultiSelectChip, SideDrawer, TextField, withStyles } from 'vopay';
import type { AccountUserRole } from 'vopay';
import { Button } from 'UI';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';
import useAccountUsers from '@/hooks/useApi/useAccountUsers';
import useAssignUserRole from '@/hooks/useApi/useAssignUserRole';
import styles from './CreateUserDrawer.module.scss';

const cn = withStyles(styles);

type CreateUserDrawerProps = {
  isOpen: boolean;
  existingUsernames: string[];
  availableRoles?: AccountUserRole[];
  onClose: () => void;
  onUserCreated?: () => void;
  /**
   * If provided, the role dropdown shows a "+ New Role" footer button. The
   * callback receives the form's current in-progress state so the parent
   * can preserve it across the Create-Role chain. Pass only when the admin
   * has `ManageUserRoles`.
   */
  onCreateNewRole?: (currentState: { emailAddress: string; selectedRoleIds: number[] }) => void;
  /**
   * Initial values applied on each open (used to restore state when the
   * drawer is reopened after the Create Role chain).
   */
  initialEmailAddress?: string;
  initialRoleIds?: number[];
  /** When set, the new user + role assignments target this sub-account instead of the logged-in account. */
  bearer?: string;
};

type RoleOption = { value: string; label: string };

export default function CreateUserDrawer({
  isOpen,
  existingUsernames,
  availableRoles = [],
  onClose,
  onUserCreated,
  onCreateNewRole,
  initialEmailAddress,
  initialRoleIds,
  bearer,
}: CreateUserDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UsersList.CreateUser' });
  const { createUser } = useAccountUsers();
  const { assignUserRole } = useAssignUserRole();
  const [, setLoading] = useDataFetchIndicator();
  const toast = useToast();
  const [emailAddress, setEmailAddress] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);

  // Track whether the drawer was previously open — we only hydrate from
  // initial props on a true closed→open transition to avoid resetting state
  // mid-edit when props change while the drawer is still open.
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setEmailAddress(initialEmailAddress ?? '');
      setSelectedRoleIds(initialRoleIds ?? []);
    }

    wasOpen.current = isOpen;
  }, [isOpen, initialEmailAddress, initialRoleIds]);

  const { isEmailValid, isDuplicateEmail, emailInlineError } = useMemo(() => {
    const valid = Yup.string().email().required().isValidSync(emailAddress);
    const duplicate = existingUsernames.some((existing) => existing.toLowerCase() === emailAddress.toLowerCase());

    const inlineError = duplicate
      ? t('duplicateEmail', {
          defaultValue: 'This email is already used. Please use a different email.',
        })
      : undefined;

    return { isEmailValid: valid, isDuplicateEmail: duplicate, emailInlineError: inlineError };
  }, [emailAddress, existingUsernames, t]);

  const roleOptions: RoleOption[] = availableRoles.map((role) => ({
    value: String(role.RoleID),
    label: role.Name,
  }));

  const selectedRoleValues = selectedRoleIds.map(String);

  function handleRolesChange(_event: ChangeEvent<HTMLInputElement>, args: { options: RoleOption[] }) {
    setSelectedRoleIds(args.options.map((opt) => Number(opt.value)));
  }

  function handleRoleDelete(value: string) {
    setSelectedRoleIds((prev) => prev.filter((id) => String(id) !== value));
  }

  async function handleCreate() {
    try {
      setLoading(true, 'creating user', 'fullScreen');
      await createUser({ EmailAddress: emailAddress, SendEmail: true }, bearer);

      // User created. If roles were selected, fire assigns in parallel; tally
      // partial failures so the toast can be honest about what happened.
      if (selectedRoleIds.length > 0) {
        const assignResults = await Promise.allSettled(
          selectedRoleIds.map((RoleID) => assignUserRole({ Username: emailAddress, RoleID }, bearer)),
        );

        const succeeded = assignResults.filter((r) => r.status === 'fulfilled').length;
        const failed = assignResults.length - succeeded;

        if (failed === 0) {
          toast.success(
            t('successMessageWithRoles', {
              defaultValue: 'User was successfully created and {{numAssigned}} role(s) assigned.',
              numAssigned: succeeded,
            }),
          );
        } else if (succeeded > 0) {
          toast.neutral(
            t('partialRolesAssigned', {
              defaultValue: 'User created. {{numAssigned}} of {{numTotal}} roles assigned ({{numFailed}} failed).',
              numAssigned: succeeded,
              numTotal: assignResults.length,
              numFailed: failed,
            }),
          );
        } else {
          toast.neutral(
            t('rolesAssignFailed', {
              defaultValue: 'User was created, but the role assignments failed.',
            }),
          );
        }
      } else {
        toast.success(t('successMessage', { defaultValue: 'User was successfully created.' }));
      }

      onUserCreated?.();
      onClose();
    } catch {
      toast.neutral(t('failMessage', { defaultValue: 'Unable to create the user.' }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Create User' })}
      footer={
        <Box className={cn('footer')}>
          <Button colorScheme="primary" disabled={!isEmailValid || isDuplicateEmail} onClick={handleCreate}>
            {t('createButton', { defaultValue: 'Create User' })}
          </Button>
        </Box>
      }
    >
      <div className={cn('body')}>
        <Card hoverEffect={false}>
          <Box flx="col" gapSize="16px">
            <ElementContainer
              label={t('emailAddress', { defaultValue: 'Email Address' })}
              required
              isBold={false}
              padding="0 0 6px"
              htmlFor="newUserEmail"
              input={
                <TextField
                  name="newUserEmail"
                  placeholder={t('emailPlaceholder', { defaultValue: 'alex.rogers@acme.com' })}
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  variant="outlineShadow"
                  error={emailInlineError}
                  errorFitContent
                />
              }
            />

            {availableRoles.length > 0 && (
              <ElementContainer
                label={t('selectRoles', { defaultValue: 'Select Role(s)' })}
                isBold={false}
                padding="0 0 6px"
                htmlFor="newUserRoles"
                input={
                  <MultiSelectChip
                    name="newUserRoles"
                    options={roleOptions}
                    value={selectedRoleValues}
                    onChange={handleRolesChange}
                    onDelete={handleRoleDelete}
                    placeholder={t('selectRolesPlaceholder', { defaultValue: 'Select' })}
                    filterable
                    emptyFilterMsg={t('noRolesFound', { defaultValue: 'No roles found' })}
                    renderOption={(o, { Option, SelectedIcon, selected }) => (
                      <Option>
                        {o.label}
                        {selected && <SelectedIcon />}
                      </Option>
                    )}
                    renderDropdownFooter={
                      onCreateNewRole
                        ? ({ close }) => (
                            <Button
                              iconLeft="add"
                              style={{ width: '100%' }}
                              onClick={() => {
                                close();
                                onCreateNewRole({ emailAddress, selectedRoleIds });
                              }}
                            >
                              {t('newRole', { defaultValue: 'New Role' })}
                            </Button>
                          )
                        : undefined
                    }
                  />
                }
              />
            )}
          </Box>
        </Card>
      </div>
    </SideDrawer>
  );
}
